import type { Template } from '../../../entities/template/model/types';
import { wrapTextLines } from '../../../shared/lib/video';

export interface TextPosition {
  x: number; y: number; previewWidth: number; previewHeight: number; textBoxWidth: number;
  /** Template-space (0..1920) distance from bottom to box bottom edge. Preferred
   *  anchor — font-independent, matches preview. Optional for backward compat. */
  bottom?: number;
}

export interface OverlayInput {
  text: string;
  logoUrl: string | null;
  textPosition: TextPosition;
  backgroundColor: string;
  backgroundAlpha?: number;
  textColor: string;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  fontFamily: string;
  fontUrl?: string;
  textUppercase: boolean;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontVariationSettings?: Record<string, number>;
  logoPosition?: Template['logoPosition'];
  textDuration?: number | 'full';
  fontSize?: number | null;
  lines?: string[];
  /** Столько первых строк покрашены целиком первым цветом. */
  primaryLines?: number;
  /** Сколько символов строки primaryLines ещё покрашены первым цветом:
   *  граница проходит посреди строки, до двоеточия. */
  splitChars?: number;
  logoSize?: number;
  lineSpacing?: number;
  frameUrl?: string; // PNG-каркас 1080×1920 поверх видео
  textAlign?: 'left' | 'center' | 'right';
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  hCenterX?: number;
}

export interface RenderTask {
  hash: string;
  accountIds: string[];
  overlay: OverlayInput | null; // null = no overlay (template-less mode); server skips overlay-renderer
  reelBackgroundUrl?: string | null;
}

export interface BuildRenderTasksInput {
  accounts: { id: string }[];
  templates: Template[];
  titles: Map<string, string>;
  textPosition: TextPosition;
  fontSizes: Map<string, number>;
  /** Сколько секунд держать заголовок (выбор 3/5/10 в форме); 'full' — всё видео. */
  textDuration?: number | 'full';
}

function overlayFromTemplate(t: Template | undefined, text: string, pos: TextPosition, fontSize: number | null, textDuration: number | 'full' = 'full'): OverlayInput {
  // Размер не выставлен руками → отдаём null, и wrapTextLines подбирает кегль
  // сам, в пикселях кадра. Раньше тут стояли зашитые 24, домноженные на
  // ширину превью: дежурный правил размер на каждом рилсе, а результат ещё и
  // зависел от ширины окна браузера.
  const wrapped = wrapTextLines({
    text,
    fontSize,
    fontFamily: t?.fontFamily ?? 'Arial',
    fontVariationSettings: t?.fontVariationSettings,
    uppercase: t?.textUppercase ?? false,
    bottom: pos.bottom ?? 0,
    previewWidth: pos.previewWidth,
    lineSpacing: t?.textLineSpacing,
    // Те же поля отступа, что читает drawOverlay при отрисовке бокса.
    textBoxPadding: t?.textBoxPadding,
    textBoxPaddingTop: t?.textBoxPaddingTop,
    textBoxPaddingLeft: t?.textBoxPaddingLeft,
    textBoxPaddingRight: t?.textBoxPaddingRight,
    maxFontSize: t?.autoFontMax,
    colonBreak: t?.colonBreak,
    textZoneWidth: t?.textZoneWidth,
    plateVisible: (t?.backgroundAlpha ?? 1) > 0.05,
  });
  return {
    text,
    logoUrl: t?.logoUrl ?? null,
    textPosition: pos,
    backgroundColor: t?.backgroundColor ?? '#000000',
    backgroundAlpha: t?.backgroundAlpha,
    textColor: t?.textColor ?? '#ffffff',
    textShadowEnabled: t?.textShadowEnabled,
    textShadowColor: t?.textShadowColor,
    textShadowBlur: t?.textShadowBlur,
    textShadowWidth: t?.textShadowWidth,
    textSoftShadowEnabled: t?.textSoftShadowEnabled,
    textSoftShadowColor: t?.textSoftShadowColor,
    textSoftShadowBlur: t?.textSoftShadowBlur,
    textSoftShadowOffsetX: t?.textSoftShadowOffsetX,
    textSoftShadowOffsetY: t?.textSoftShadowOffsetY,
    textSoftShadowOpacity: t?.textSoftShadowOpacity,
    textShadowOpacity: t?.textShadowOpacity,
    fontFamily: t?.fontFamily ?? 'Arial',
    fontUrl: t?.fontUrl,
    textUppercase: t?.textUppercase ?? false,
    useTwoColors: t?.useTwoColors,
    textColorSecondary: t?.textColorSecondary,
    fontVariationSettings: t?.fontVariationSettings,
    logoPosition: t?.logoPosition,
    textDuration,
    fontSize: wrapped.fontSize,
    lines: wrapped.lines,
    primaryLines: wrapped.primaryLines,
    splitChars: wrapped.splitChars,
    logoSize: t?.logoSize,
    lineSpacing: t?.textLineSpacing,
    frameUrl: t?.frameUrl,
    textAlign: t?.textAlign,
    textBoxStyle: t?.textBoxStyle,
    textBoxAngle: t?.textBoxAngle,
    textBoxRadius: t?.textBoxRadius,
    textBoxPadding: t?.textBoxPadding,
    textBoxPaddingTop: t?.textBoxPaddingTop,
    textBoxPaddingLeft: t?.textBoxPaddingLeft,
    textBoxPaddingRight: t?.textBoxPaddingRight,
    hCenterX: t?.reelTextCenterX ?? 0,
  };
}

function stableHash(overlay: OverlayInput): string {
  const json = JSON.stringify(overlay, Object.keys(overlay).sort());
  let h = 0;
  for (let i = 0; i < json.length; i++) { h = (h * 31 + json.charCodeAt(i)) | 0; }
  return String(h >>> 0);
}

export function buildRenderTasks(input: BuildRenderTasksInput): RenderTask[] {
  return input.accounts.map((account) => {
    const t = input.templates.find(
      (tpl) => tpl.accountId === account.id && (tpl.type ?? 'reel') === 'reel',
    );
    const size = input.fontSizes.get(account.id) ?? null;
    const overlay = overlayFromTemplate(t, input.titles.get(account.id) ?? '', input.textPosition, size, input.textDuration);
    return { hash: account.id, accountIds: [account.id], overlay, reelBackgroundUrl: t?.reelBackgroundUrl ?? null };
  });
}

// Template-less mode: one render task with no overlay, all accounts share the output video.
export function buildPlainRenderTask(accounts: { id: string }[]): RenderTask {
  return { hash: 'plain', accountIds: accounts.map((a) => a.id), overlay: null };
}
